import React, { useCallback, useEffect, useState } from 'react';
import { Shuffle, RotateCcw, RefreshCw, Loader2 } from 'lucide-react';

/**
 * Fill Zero Traffic (admin).
 *
 * One button that replaces zero traffic in affiliate_clean with a random
 * 100–300: countries at 0 in the geo breakdown, organic traffic at 0 (set to
 * the geo breakdown's sum when the row has one) and total traffic. Paid
 * traffic is left as it is. The latest run can be undone.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const BASE = `${API_URL}/api/admin/traffic-zero-fill`;

const request = async (path, options = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options
  });
  return response.json();
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const formatWhen = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg border border-gray-200 p-4 dark:border-slate-800">
    <div className="text-2xl font-semibold text-gray-900 dark:text-slate-100">{fmt(value)}</div>
    <div className="mt-1 font-medium text-gray-700 dark:text-slate-300">{label}</div>
    {hint && <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">{hint}</div>}
  </div>
);

const TrafficZeroFill = () => {
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [range, setRange] = useState([100, 300]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'run' | 'undo'
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('');
      if (data.success) {
        setPreview(data.preview);
        setRuns(data.runs || []);
        if (data.range) setRange(data.range);
      } else {
        setMessage({ ok: false, text: data.message || 'Failed to load' });
      }
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRun = async () => {
    if (!preview?.rows) return;
    if (!window.confirm(
      `Replace zero traffic in ${fmt(preview.rows)} affiliate rows with a random ${range[0]}–${range[1]}?\n\n` +
      'You can undo this run afterwards from this page.'
    )) return;
    setBusy('run');
    setMessage(null);
    try {
      const data = await request('/run', { method: 'POST' });
      setMessage(data.success
        ? { ok: true, text: `Updated ${fmt(data.updated)} row${data.updated === 1 ? '' : 's'}.` }
        : { ok: false, text: data.message || 'Failed to fill zero traffic' });
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(null);
      load();
    }
  };

  const handleUndo = async (run) => {
    if (!window.confirm(`Undo the run from ${formatWhen(run.created_at)} and put back the zeros in ${fmt(run.rows_changed)} rows?`)) return;
    setBusy('undo');
    setMessage(null);
    try {
      const data = await request('/undo', { method: 'POST' });
      setMessage(data.success
        ? { ok: true, text: `Restored ${fmt(data.restored)} row${data.restored === 1 ? '' : 's'}. Rows re-scraped since the run keep their new data.` }
        : { ok: false, text: data.message || 'Failed to undo' });
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(null);
      load();
    }
  };

  const latestUndoable = runs.find((r) => !r.undone_at);

  return (
    <div className="p-6">
      <div className="max-w-5xl">
        <div className="mb-1 flex items-center gap-3">
          <Shuffle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Fill Zero Traffic</h1>
        </div>
        <p className="mb-6 text-gray-600 dark:text-slate-400">
          Replaces zero traffic in the affiliate list with a random {range[0]}–{range[1]}. A country showing 0 in the
          geographic breakdown gets a random number; organic traffic at 0 becomes the sum of the countries (or a random
          number when the site has no countries); total traffic is raised to at least organic + paid. Paid traffic is
          not changed.
        </p>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-slate-100">What the next run will change</h2>
            <button
              type="button"
              onClick={load}
              disabled={loading || !!busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Recount
            </button>
          </div>

          {loading && !preview ? (
            <p className="text-gray-500">Counting…</p>
          ) : preview ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Sites with a country at 0" value={preview.geoRows} hint={`each 0 → random ${range[0]}–${range[1]}`} />
              <Stat label="Organic 0 → sum of countries" value={preview.organicFromGeo} />
              <Stat label="Organic 0, no countries" value={preview.organicRandom} hint={`→ random ${range[0]}–${range[1]}`} />
              <Stat label="Total traffic updated" value={preview.totalRows} />
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleRun}
              disabled={!!busy || loading || !preview?.rows}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy === 'run' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
              {preview?.rows ? `Fill zeros in ${fmt(preview.rows)} rows` : 'No zero traffic to fill'}
            </button>
          </div>

          {message && (
            <p className={`mt-3 text-sm ${message.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {message.text}
            </p>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="min-w-full text-left">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">Run</th>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3 text-right">Rows</th>
                <th className="px-4 py-3 text-right">Countries</th>
                <th className="px-4 py-3 text-right">Organic</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {runs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No runs yet.</td></tr>
              )}
              {runs.map((run) => (
                <tr key={run.id} className="text-gray-800 dark:text-slate-200">
                  <td className="px-4 py-3 text-gray-500">{run.id}</td>
                  <td className="px-4 py-3">{formatWhen(run.created_at)}</td>
                  <td className="px-4 py-3 text-right">{fmt(run.rows_changed)}</td>
                  <td className="px-4 py-3 text-right">{fmt(run.geo_rows)}</td>
                  <td className="px-4 py-3 text-right">{fmt(run.organic_rows)}</td>
                  <td className="px-4 py-3 text-right">{fmt(run.total_rows)}</td>
                  <td className="px-4 py-3 text-right">
                    {run.undone_at ? (
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        Undone {formatWhen(run.undone_at)} ({fmt(run.undone_rows)} restored)
                      </span>
                    ) : run.id === latestUndoable?.id ? (
                      <button
                        type="button"
                        onClick={() => handleUndo(run)}
                        disabled={!!busy}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        {busy === 'undo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                        Undo
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrafficZeroFill;
